'use client';
import {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import {
  formatDateOnly,
} from '@/utils/date';
import isNil from 'lodash/isNil';

type UseUrlSyncedDateResult = {
  selectedDate: Dayjs;
  setSelectedDate: (nextDate: Dayjs) => void;
};

/**
 * Keeps a Dayjs date value in sync with the URL query string.
 * - Reads the initial value from `?date=YYYY-MM-DD` (or provided key)
 * - Updates the URL (replace) only when caller sets a new date
 * - Listens for back/forward and restores the date from the URL
 */
export default function useUrlSyncedDate(
  queryKey: string = 'date',
  fallbackDate: Dayjs = dayjs(),
): UseUrlSyncedDateResult {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const parsedDateFromQuery = useMemo(() => {
    const rawQueryValue = searchParams?.get(queryKey);
    const parsed = rawQueryValue ? dayjs(rawQueryValue) : null;
    return parsed && parsed.isValid() ? parsed : null;
  }, [searchParams, queryKey]);

  const [selectedDate, setSelectedDateState] = useState<Dayjs>(parsedDateFromQuery ?? fallbackDate);

  // Keep local state updated when navigating via history (back/forward)
  useEffect(() => {
    if (!isNil(parsedDateFromQuery) && !selectedDate.isSame(parsedDateFromQuery, 'day')) {
      setSelectedDateState(parsedDateFromQuery);
    }
  }, [parsedDateFromQuery?.valueOf()]);

  // Setter that also persists to URL, but only when caller changes the date
  const setSelectedDate = useCallback((nextDate: Dayjs) => {
    setSelectedDateState(nextDate);
    const currentParams = new URLSearchParams(searchParams?.toString());
    const currentValue = currentParams.get(queryKey);
    const nextValue = formatDateOnly(nextDate);
    if (currentValue !== nextValue) {
      currentParams.set(queryKey, nextValue);
      router.replace(`${pathname}?${currentParams.toString()}`, { scroll: false });
    }
  }, [router, pathname, searchParams, queryKey]);

  return { selectedDate, setSelectedDate };
}
